import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Spinner from '../../components/ui/Spinner';
import { useAuthStore } from '../../store/authStore';
import { getErrorMessage } from '../../lib/apiClient';
import { refreshRequest } from './authApi';

/**
 * OAuth landing page. The backend redirects here with tokens (or an error) in
 * the URL FRAGMENT — fragments never reach servers or logs. The refresh token
 * is immediately exchanged for a fresh pair (which also returns the user), the
 * session is stored, and `replace` navigation scrubs the tokens from history.
 */
export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    const params = new URLSearchParams(window.location.hash.slice(1));
    const error = params.get('error');
    const refreshToken = params.get('refreshToken');
    const challengeToken = params.get('challengeToken');
    const isNewUser = params.get('newUser') === '1';

    // Two-step is on for this account: finish on the sign-in page's code step.
    if (challengeToken) {
      navigate('/login', { replace: true, state: { challengeToken } });
      return;
    }

    if (error || !refreshToken) {
      toast.error(error || 'Google sign-in failed');
      navigate('/login', { replace: true });
      return;
    }

    refreshRequest(refreshToken)
      .then((data) => {
        setSession(data);
        toast.success(`Welcome, ${data.user.displayName}`);
        // First sign-in → one-time "name your organization" onboarding.
        navigate(isNewUser ? '/welcome' : '/files', { replace: true });
      })
      .catch((err) => {
        toast.error(getErrorMessage(err, 'Google sign-in failed'));
        navigate('/login', { replace: true });
      });
  }, [navigate, setSession]);

  return (
    <div className="flex h-screen items-center justify-center bg-slate-50">
      <Spinner />
    </div>
  );
}
